import { createSlice } from '@reduxjs/toolkit';
import { authApi } from './authApi';
import { normalizeUser } from './normalizeUser';

const initialState = {
  user: null,
  status: 'idle', // idle | authenticated | unauthenticated
  // True once the app-load refresh+me bootstrap check (see ProtectedRoute /
  // App.jsx) has resolved either way — lets the whole app (not just
  // protected routes) tell "still checking" apart from "definitely logged
  // out", so e.g. the public Home page doesn't flash a logged-out nav for
  // a returning user.
  bootstrapped: false,
  // See setBootstrapWarning below.
  bootstrapWarning: null,
};

// POST /auth/login can return either a TwoFactorChallengeResponse
// (requires2fa: true, no tokens yet — see Login.jsx for the field-name
// note) or a full SessionResponse — only the latter should ever mark the
// user as authenticated.
function applySession(state, payload) {
  if (payload?.access_token && payload?.user) {
    state.user = normalizeUser(payload.user);
    state.status = 'authenticated';
  }
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuth(state) {
      state.user = null;
      state.status = 'unauthenticated';
    },
    setBootstrapped(state) {
      state.bootstrapped = true;
    },
    // Set when a page reload had a valid refresh token (proving a real
    // session existed) but GET /auth/me itself failed with something other
    // than "your token is bad" — e.g. a 404, which the live API currently
    // returns for that route (see useAuthBootstrap). We still can't safely
    // treat the visitor as logged in without knowing their role, so this
    // just lets the login page explain *why* they landed back here instead
    // of silently looking identical to a first-time visit.
    setBootstrapWarning(state, action) {
      state.bootstrapWarning = action.payload ?? null;
    },
    // Dispatched by authAxios's response interceptor the instant ANY
    // protected call — not just GET /auth/me — comes back 403 with
    // mustChangePassword: true. The docs are explicit this flag can gate
    // every protected endpoint, so catching it only on the /auth/me
    // bootstrap check would miss it if it starts appearing mid-session on
    // some other call (e.g. right after an admin resets this account's
    // password). ProtectedRoute reacts to this the same render it changes,
    // since it already reads selectMustChangePassword on every render.
    forceMustChangePassword(state) {
      if (!state.user) return; // no session to attach the flag to
      state.user.must_change_password = true;
      state.status = 'authenticated';
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(authApi.endpoints.login.matchFulfilled, (state, action) => {
        applySession(state, action.payload);
      })
      .addMatcher(authApi.endpoints.verifyTwoFactor.matchFulfilled, (state, action) => {
        applySession(state, action.payload);
      })
      .addMatcher(authApi.endpoints.consumeMagicLink.matchFulfilled, (state, action) => {
        applySession(state, action.payload);
      })
      .addMatcher(authApi.endpoints.me.matchFulfilled, (state, action) => {
        state.user = normalizeUser(action.payload?.user);
        state.status = 'authenticated';
      })
      .addMatcher(authApi.endpoints.me.matchRejected, (state, action) => {
        // GET /auth/me is itself subject to the forced-password-change
        // gate (403 { mustChangePassword: true }, no user body) — without
        // this branch, reloading the page while that flag is pending would
        // look identical to "not logged in" and bounce the user to /login
        // instead of /change-password. A valid access token got them past
        // the 401 check to reach this 403 at all, so they ARE logged in.
        if (action.payload?.data?.mustChangePassword) {
          state.user = { ...(state.user ?? {}), must_change_password: true };
          state.status = 'authenticated';
          return;
        }
        state.user = null;
        state.status = 'unauthenticated';
      })
      .addMatcher(authApi.endpoints.changePassword.matchFulfilled, (state) => {
        if (state.user) state.user.must_change_password = false;
      })
      .addMatcher(authApi.endpoints.logout.matchFulfilled, (state) => {
        state.user = null;
        state.status = 'unauthenticated';
      });
  },
});

export const { clearAuth, setBootstrapped, setBootstrapWarning, forceMustChangePassword } = authSlice.actions;

export const selectCurrentUser = (state) => state.auth.user;
export const selectAuthStatus = (state) => state.auth.status;
export const selectIsBootstrapped = (state) => state.auth.bootstrapped;
export const selectUserRoles = (state) => state.auth.user?.roles ?? [];
export const selectHasRole = (role) => (state) => selectUserRoles(state).includes(role);
export const selectMustChangePassword = (state) => !!state.auth.user?.must_change_password;
export const selectBootstrapWarning = (state) => state.auth.bootstrapWarning;

export default authSlice.reducer;
